import { type QueryClient, useQuery } from '@tanstack/react-query';

import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import OffersService from '~/services/OffersService';
import type { CMChecklistManualOverride } from '~/services/OffersService';

import type ApiError from '~/types/ApiError';

import { offerQueryKeys } from './useGetOffer';

// Nested under `offerQueryKeys.all` so the CRM resync completion invalidates it with the rest.
export const cmChecklistEvaluationQueryKeys = {
  all: [...offerQueryKeys.all, 'cm-checklist-evaluation'] as const,
  detail: (offerId: string | undefined) => [...cmChecklistEvaluationQueryKeys.all, offerId] as const,
};

export function useGetCMChecklistEvaluation(
  offerId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery<CmChecklist.Evaluation>({
    queryKey: cmChecklistEvaluationQueryKeys.detail(offerId),
    queryFn: async ({ signal }) => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      const response = await OffersService.getCMChecklistEvaluation(offerId, { signal });
      return response.result;
    },
    enabled: enabled && !!offerId,
    // A 404 means the offer hasn't synced a checklist yet, not a transient failure - retrying it
    // just delays showing that state to the CM.
    retry: (failureCount, error) => (error as ApiError).status !== 404 && failureCount < 2,
  });
}

function withManualOverride<T extends CmChecklist.Row>(
  row: T,
  effectiveStatus: CmChecklist.Row['effective_status'],
  manualOverride: NonNullable<CmChecklist.Row['manual_override']>,
): T {
  return { ...row, effective_status: effectiveStatus, manual_override: manualOverride };
}

// Reflects a saved override in the cached evaluation so the UI settles without a refetch.
export function applyManualOverrideToEvaluation(
  evaluation: CmChecklist.Evaluation,
  manualOverride: CMChecklistManualOverride,
): CmChecklist.Evaluation {
  return {
    ...evaluation,
    rows: evaluation.rows.map((row) => {
      if (row.item_key !== manualOverride.item_key) {
        return row;
      }

      const matchesCurrentEvidence = row.evidence_hash === manualOverride.evidence_hash;

      return withManualOverride(
        row,
        matchesCurrentEvidence ? (manualOverride.is_approved ? 'approved' : 'requires_approval') : row.status,
        {
          evidence_hash: manualOverride.evidence_hash,
          is_approved: manualOverride.is_approved,
          matches_current_evidence: matchesCurrentEvidence,
          updated_at: manualOverride.updated_at,
          updated_by: manualOverride.updated_by,
        },
      );
    }),
  };
}

export function patchCMChecklistEvaluationOverride(
  queryClient: QueryClient,
  offerId: string | undefined,
  manualOverride: CMChecklistManualOverride,
) {
  queryClient.setQueryData<CmChecklist.Evaluation>(cmChecklistEvaluationQueryKeys.detail(offerId), (evaluation) =>
    evaluation ? applyManualOverrideToEvaluation(evaluation, manualOverride) : evaluation,
  );
}
